import React, { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from '@chakra-ui/react';
import type { ISpAuthorizationToken } from 'ts/client/common';
import { useCoverSlideshowLoader } from 'ts/client/gallery/hooks';
import { ClientGalleryServiceProvider } from 'ts/common/contexts/ClientGalleryServiceContext';
import { CoverSlideshowProvider } from 'ts/common/contexts/CoverSlideshowContext';
import type { ICoverPhotoBase } from 'ts/common/types';

interface Props {
    coverPhoto?: ICoverPhotoBase;
    /** Album context for the cover slideshow's authorization token. Omit on the gallery home header. */
    albumId?: number | string | null;
    /**
     * Reports that the header has everything it needs to render its final state — either the cover
     * slideshow is loaded and ready to play, loading it failed (so the static poster is final), or
     * there was never a slideshow to load at all.
     *
     * Exists so the surrounding AngularJS page can keep its own route-transition loading state up
     * until the slideshow can start on the first painted frame, instead of revealing the header on
     * its poster and swapping to the slideshow a beat later. Called at most once per mount, and
     * always eventually called, so a caller gating on it can never be stranded.
     */
    onCoverSlideshowReady?: () => void;
    spAuthorizationToken: ISpAuthorizationToken;
    spEventBus: SpAngularJs.ISpEventBus;
    children: ReactNode;
}

interface ILoaderProps {
    coverPhoto: ICoverPhotoBase;
    albumId?: number | string | null;
    onCoverSlideshowReady?: () => void;
    children: ReactNode;
}

/**
 * Mounted only when the cover really is a slideshow — loading one reads the client gallery's
 * event data, which a plain photo cover has no reason to require. A separate component (rather
 * than a hook called unconditionally from `CoverSlideshowHeaderGate`) so the loader itself only
 * mounts when it applies, mirroring `CoverSlideshowGate`'s own split for the intro page.
 *
 * Rendering is never gated on `isSettled` — the provider value stays `null` until the slideshow is
 * ready and `CoverPhoto` paints the static poster meanwhile, so the header always has something to
 * show. `isSettled` is instead reported outward via `onCoverSlideshowReady`, letting a caller that
 * *does* have a loading state of its own (the AngularJS route transition) hide that gap entirely.
 */
const HeaderCoverSlideshowLoader: FC<ILoaderProps> = ({
    coverPhoto,
    albumId,
    onCoverSlideshowReady,
    children
}) => {
    const { coverSlideshow, isSettled } = useCoverSlideshowLoader(coverPhoto, {
        albumId,
        isSilent: true
    });

    // `isSettled` covers a failed load as well as a ready one, so a slideshow that can never play
    // still releases the caller rather than stranding it on a loading state forever.
    useEffect(() => {
        if (isSettled) {
            onCoverSlideshowReady?.();
        }
    }, [isSettled, onCoverSlideshowReady]);

    return <CoverSlideshowProvider value={coverSlideshow}>{children}</CoverSlideshowProvider>;
};

/**
 * Makes a slideshow gallery/album cover play inside the client-gallery header (`PageTitle` /
 * `AlbumPageTitle` / `HomePageTitle`'s `CoverPhoto`).
 *
 * Those components are mounted into AngularJS via `react2angular` and, unlike the intro page,
 * have neither `ClientGalleryServiceContext` nor `CoverSlideshowContext` available on their own —
 * this always provides the former, and provides the latter only when the cover actually is a
 * slideshow the visitor is eligible to see play (same conditions as `Intro.tsx`: not reduced
 * motion, and a real `slideshowPhotoId`).
 *
 * Always silent: inside the gallery the playlist owns sound, so the header slideshow only ever
 * plays muted and never claims or loses audio over gallery music (see `isSilent` on
 * `CoverSlideshowContext`).
 */
const CoverSlideshowHeaderGate: FC<Props> = ({
    coverPhoto,
    albumId,
    onCoverSlideshowReady,
    spAuthorizationToken,
    spEventBus,
    children
}) => {
    const prefersReducedMotion = usePrefersReducedMotion();

    const coverSlideshowToLoad =
        !prefersReducedMotion &&
        coverPhoto &&
        coverPhoto.mediaType === 'slideshow' &&
        coverPhoto.slideshowPhotoId
            ? coverPhoto
            : null;

    // Readiness is a one-way latch: the caller reveals the page on the first report and has no use
    // for later ones, and this guard keeps a re-rendered (or re-identified) callback prop from
    // firing it again.
    const hasReportedReadyRef = useRef(false);
    const reportReadyOnce = useCallback(() => {
        if (hasReportedReadyRef.current) {
            return;
        }

        hasReportedReadyRef.current = true;
        onCoverSlideshowReady?.();
    }, [onCoverSlideshowReady]);

    // Nothing to wait for — report ready immediately so a caller gating on this never delays a
    // plain photo cover, a reduced-motion visitor, or a header with no cover at all.
    useEffect(() => {
        if (!coverSlideshowToLoad) {
            reportReadyOnce();
        }
    }, [coverSlideshowToLoad, reportReadyOnce]);

    const content = coverSlideshowToLoad ? (
        <HeaderCoverSlideshowLoader
            coverPhoto={coverSlideshowToLoad}
            albumId={albumId}
            onCoverSlideshowReady={reportReadyOnce}
        >
            {children}
        </HeaderCoverSlideshowLoader>
    ) : (
        children
    );

    return (
        <ClientGalleryServiceProvider
            spAuthorizationToken={spAuthorizationToken}
            spEventBus={spEventBus}
        >
            {content}
        </ClientGalleryServiceProvider>
    );
};

export default CoverSlideshowHeaderGate;
